import { useQueryFetch } from '@shared/hooks/useQueryFetch';
import { ProcessField, ProcessFieldRaw } from '@shared/types/process';
import { parseFieldDefaultValue } from '@shared/util/flowParseValue';

import { getQueryKey } from '@shadcn-features/query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

const parseProcessField = (field: ProcessFieldRaw): ProcessField => {
    const fieldParsed: ProcessField = {
        ...field,
        options:
            typeof field.options === 'string'
                ? JSON.parse(field.options || '{}')
                : field.options,
    };

    return {
        ...fieldParsed,
        defaultValue: parseFieldDefaultValue(fieldParsed),
    };
};

const normalizeProcessFields = (data?: ProcessFieldRaw[]): ProcessField[] => {
    const fields = Array.isArray(data) ? data : [];

    return fields.map(parseProcessField);
};

export const useLayoutProcessFields = ({
    serviceId,
    layoutId,
}: {
    serviceId?: string;
    layoutId?: string;
}) => {
    const queryClient = useQueryClient();

    const fieldsQuery = useQueryFetch<ProcessFieldRaw[]>(
        `flow/ServiceProcessFields/GetAllByServiceId`,
        {
            params: {
                ServiceId: serviceId,
                ServiceProcessLayoutId: layoutId,
            },
            enabled: !!serviceId && !!layoutId,
        },
    );

    useEffect(() => {
        if (!fieldsQuery.data?.length) return;

        for (const field of fieldsQuery.data) {
            queryClient.setQueryData(
                getQueryKey({
                    url: `flow/ServiceProcessFields/Get/${field.id}`,
                }),
                field,
            );
        }
    }, [fieldsQuery.data, queryClient]);

    const normalizedFields = useMemo(() => {
        return normalizeProcessFields(fieldsQuery.data);
    }, [fieldsQuery.data]);

    const processFieldsById = useMemo(() => {
        return normalizedFields.reduce<Record<string, ProcessField>>(
            (acc, field) => {
                acc[field.id] = field;
                return acc;
            },
            {},
        );
    }, [normalizedFields]);

    return {
        isLoading: fieldsQuery.isFetching,
        processFieldsById,
    };
};
